import { useState } from "react";

import { ChevronDownIcon } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";

import Error from "@/shared/components/Error";
import { cn } from "@/shared/lib/utils/cn";
import { AppError } from "@/shared/lib/utils/errors";

interface Props {
  label: string;
  icon: React.ElementType;
  options: string[];
  selected: string[];
  onApply: (values: string[]) => void;
  isLoading?: boolean;
  isFetchingNextPage?: boolean;
  hasNextPage?: boolean;
  onLoadMore?: () => void;
  error?: AppError | Error | null;
  errorModalOpen?: boolean;
  onErrorModalOpenChange?: (open: boolean) => void;
  onRetry?: () => void;
}

export function FilterPopover({ label, icon: Icon, options, selected, onApply, isLoading, isFetchingNextPage, hasNextPage, onLoadMore, error, errorModalOpen, onErrorModalOpenChange, onRetry }: Props) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<string[]>(selected);

  const hasSelection = selected.length > 0;

  function handleOpenChange(next: boolean) {
    if (next) setDraft(selected);
    setOpen(next);
  }

  function toggleDraft(value: string) {
    setDraft((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
  }

  function handleApply() {
    onApply(draft);
    setOpen(false);
  }

  function handleClear() {
    setDraft([]);
  }

  return (
    <>
      {error && errorModalOpen !== undefined && onErrorModalOpenChange && <Error variant="modal" title="Failed to load options" error={error} open={errorModalOpen} onOpenChange={onErrorModalOpenChange} onRetry={onRetry} />}
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="sm" className={cn("gap-1.5 text-xs font-medium", hasSelection ? "text-primary" : "text-muted-foreground")}>
            <Icon className="size-3.5 shrink-0" />
            {label}
            {hasSelection && <Badge className="ml-0.5 size-4 justify-center p-0 text-[10px]">{selected.length}</Badge>}
            <ChevronDownIcon className="size-3 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-48 gap-0 p-0">
          <p className="px-3 py-2 text-xs font-semibold tracking-wider text-muted-foreground uppercase">{label}</p>
          <Separator />
          {isLoading ? (
            <div className="flex flex-col gap-2 px-3 py-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="flex items-center gap-2.5">
                  <Skeleton className="size-4 shrink-0" />
                  <Skeleton className="h-3.5 w-24" />
                </div>
              ))}
            </div>
          ) : options.length === 0 ? (
            <p className="px-3 py-4 text-center text-xs text-muted-foreground">No options available.</p>
          ) : (
            <div className="flex flex-col py-1">
              {options.map((option) => (
                <label key={option} className="flex cursor-pointer items-center gap-2.5 px-3 py-2 text-sm transition-colors hover:bg-accent">
                  <Checkbox checked={draft.includes(option)} onCheckedChange={() => toggleDraft(option)} />
                  <span className="truncate">{option}</span>
                </label>
              ))}
              {hasNextPage && (
                <button onClick={onLoadMore} disabled={isFetchingNextPage} className="px-3 py-2 text-left text-xs text-muted-foreground transition-colors hover:text-foreground disabled:pointer-events-none disabled:opacity-40">
                  {isFetchingNextPage ? "Loading..." : "Load more"}
                </button>
              )}
            </div>
          )}
          <Separator />
          <div className="flex items-center justify-between gap-2 px-3 py-2">
            <button onClick={handleClear} disabled={draft.length === 0} className="text-xs text-muted-foreground transition-colors hover:text-foreground disabled:pointer-events-none disabled:opacity-40">
              Clear
            </button>
            <Button size="sm" className="h-7 text-xs" onClick={handleApply} disabled={isLoading || !!error}>
              Apply
            </Button>
          </div>
        </PopoverContent>
      </Popover>
    </>
  );
}
